import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import {
    Box, Button, Typography, TextField, Alert, Container,
    Paper, Avatar, IconButton, useTheme
} from '@mui/material';
import { QRCodeCanvas } from 'qrcode.react';
import * as OTPAuth from 'otpauth';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../index';

// Icons
import GoogleIcon from '@mui/icons-material/Google';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// [SECURITY] Storage Keys for Persistent Lockout
const LOCKOUT_KEY = 'sec_lock_ts';
const ATTEMPT_KEY = 'sec_fail_cnt';

const Login = () => {
    const { login, set2FAVerified } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme(); // [NEW] Use theme hook

    // UI States: 'LOGIN', 'SETUP_2FA', 'VERIFY_2FA'
    const [step, setStep] = useState('LOGIN');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // 2FA Data
    const [tempSecret, setTempSecret] = useState('');
    const [otpToken, setOtpToken] = useState('');
    const [authUrl, setAuthUrl] = useState('');
    const [userUid, setUserUid] = useState(null);

    // [SECURITY] Persistent Lockout State
    const [isLockedOut, setIsLockedOut] = useState(false);
    const [lockoutTimer, setLockoutTimer] = useState(0);

    // [SECURITY] Check Lockout on Mount & Interval
    useEffect(() => {
        checkLockoutStatus();
        const interval = setInterval(checkLockoutStatus, 1000);
        return () => clearInterval(interval);
    }, []);

    const checkLockoutStatus = () => {
        const lockoutUntil = localStorage.getItem(LOCKOUT_KEY);
        if (lockoutUntil) {
            const remaining = parseInt(lockoutUntil) - Date.now();
            if (remaining > 0) {
                setIsLockedOut(true);
                setLockoutTimer(Math.ceil(remaining / 1000));
            } else {
                // Lockout expired
                localStorage.removeItem(LOCKOUT_KEY);
                localStorage.removeItem(ATTEMPT_KEY);
                setIsLockedOut(false);
                setLockoutTimer(0);
            }
        }
    };

    const handleFailedAttempt = async (uid) => {
        let attempts = parseInt(localStorage.getItem(ATTEMPT_KEY) || '0') + 1;
        localStorage.setItem(ATTEMPT_KEY, attempts.toString());

        if (attempts >= 3) {
            // Lock out for 15 minutes
            const lockoutTime = Date.now() + (15 * 60 * 1000);
            localStorage.setItem(LOCKOUT_KEY, lockoutTime.toString());
            setIsLockedOut(true);

            // Log Security Event
            if (uid) {
                try {
                    await setDoc(doc(db, "security_logs", `${uid}_${Date.now()}`), {
                        uid: uid,
                        event: '2FA_LOCKED',
                        reason: 'Brute force attempt (Persistent)',
                        timestamp: serverTimestamp()
                    });
                } catch (e) { console.error("Log failed", e); }
            }
            setError("Too many failed attempts. Account locked for 15 minutes.");
        } else {
            setError(`Invalid Code. Attempts remaining: ${3 - attempts}`);
        }
    };

    // --- LOGIC: Google Sign In ---
    const handleGoogleLogin = async () => {
        setLoading(true);
        setError('');
        try {
            const user = await login();
            setUserUid(user.uid);

            // Check for Admin 2FA
            const adminRef = doc(db, "admins", user.uid);
            const adminSnap = await getDoc(adminRef);

            if (adminSnap.exists()) {
                const data = adminSnap.data();
                if (data.twoFactorSecret) {
                    setTempSecret(data.twoFactorSecret);
                    setStep('VERIFY_2FA');
                } else {
                    initiateSetup(user.email);
                }
            } else {
                // [NEW] Check if this is a temp admin — they skip 2FA entirely
                // The login() function already verified temp admin status and set adminData
                // If login() succeeded without throwing, this user has valid temp admin access
                set2FAVerified(true);
                navigate('/');
            }
        } catch (err) {
            setError(err.message || 'Access Denied');
        } finally {
            setLoading(false);
        }
    };

    // --- LOGIC: Setup 2FA ---
    const initiateSetup = (email) => {
        const secret = new OTPAuth.Secret({ size: 20 });
        const secretBase32 = secret.base32;
        setTempSecret(secretBase32);

        const totp = new OTPAuth.TOTP({
            issuer: "RESIDO",
            label: email,
            algorithm: "SHA1",
            digits: 6,
            period: 30,
            secret: secret
        });

        setAuthUrl(totp.toString());
        setStep('SETUP_2FA');
    };

    // --- LOGIC: Verify Code ---
    const verifyCode = async () => {
        if (isLockedOut) {
            setError(`Account locked. Try again in ${lockoutTimer}s`);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const totp = new OTPAuth.TOTP({
                issuer: "RESIDO",
                algorithm: "SHA1",
                digits: 6,
                period: 30,
                secret: OTPAuth.Secret.fromBase32(tempSecret)
            });

            // [SECURITY] Tightest possible window (0) to prevent replay/drift attacks
            const delta = totp.validate({ token: otpToken, window: 0 });

            if (delta === null) {
                await handleFailedAttempt(userUid);
                setLoading(false);
                return;
            }

            // Success - Clear Attempts
            localStorage.removeItem(ATTEMPT_KEY);

            if (step === 'SETUP_2FA') {
                await updateDoc(doc(db, "admins", userUid), {
                    twoFactorSecret: tempSecret,
                    isTwoFactorEnabled: true
                });
            }

            set2FAVerified(true);
            navigate('/');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(tempSecret);
        alert("Secret copied to clipboard!");
    };

    // --- ANIMATION VARIANTS ---
    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
        exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
                : `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.background.default} 100%)`,
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Animated Background Blobs */}
            <Box sx={{ position: 'absolute', top: '-10%', left: '-10%', width: '40vw', height: '40vw', bgcolor: 'primary.dark', opacity: 0.2, filter: 'blur(100px)', borderRadius: '50%' }} />
            <Box sx={{ position: 'absolute', bottom: '-10%', right: '-10%', width: '30vw', height: '30vw', bgcolor: 'secondary.dark', opacity: 0.2, filter: 'blur(80px)', borderRadius: '50%' }} />

            <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 2 }}>
                <AnimatePresence mode='wait'>
                    <motion.div
                        key={step}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        <Paper elevation={theme.palette.mode === 'dark' ? 24 : 3} sx={{
                            p: 4,
                            borderRadius: 4,
                            bgcolor: theme.palette.background.paper,
                            backdropFilter: 'blur(12px)',
                            border: `1px solid ${theme.palette.divider}`,
                            color: theme.palette.text.primary,
                            textAlign: 'center'
                        }}>

                            {/* --- HEADER ICON --- */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <Avatar sx={{
                                    width: 64, height: 64,
                                    bgcolor: isLockedOut ? 'error.main' : (step === 'LOGIN' ? 'primary.main' : step === 'SETUP_2FA' ? 'warning.main' : 'success.main'),
                                    boxShadow: theme.shadows[4]
                                }}>
                                    {isLockedOut ? <LockIcon fontSize="large" sx={{ color: 'white' }} /> : (step === 'LOGIN' ? <LockIcon fontSize="large" sx={{ color: 'white' }} /> : step === 'SETUP_2FA' ? <SecurityIcon fontSize="large" sx={{ color: 'white' }} /> : <VerifiedUserIcon fontSize="large" sx={{ color: 'white' }} />)}
                                </Avatar>
                            </Box>

                            {/* --- TITLE --- */}
                            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ letterSpacing: 1 }}>
                                {isLockedOut ? "Security Lockout" : (step === 'LOGIN' ? "Admin Portal" : step === 'SETUP_2FA' ? "Setup 2FA" : "Verification")}
                            </Typography>

                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                                {isLockedOut ? `Try again in ${lockoutTimer}s` : (
                                    step === 'LOGIN' ? "Sign in securely to manage the dashboard." :
                                        step === 'SETUP_2FA' ? "Scan the QR code with Google Authenticator." :
                                            "Enter the 6-digit code from your app."
                                )}
                            </Typography>

                            {error && (
                                <Alert severity="error" variant="filled" sx={{ mb: 3, borderRadius: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {!isLockedOut && (
                                <>
                                    {/* --- STEP 1: LOGIN --- */}
                                    {step === 'LOGIN' && (
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            size="large"
                                            startIcon={<GoogleIcon />}
                                            onClick={handleGoogleLogin}
                                            disabled={loading}
                                            sx={{
                                                bgcolor: theme.palette.mode === 'dark' ? 'white' : 'primary.main',
                                                color: theme.palette.mode === 'dark' ? 'black' : 'white',
                                                py: 1.5, borderRadius: 2, fontWeight: 'bold',
                                                textTransform: 'none', fontSize: '1rem',
                                                '&:hover': {
                                                    bgcolor: theme.palette.mode === 'dark' ? '#f1f5f9' : 'primary.dark',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: theme.shadows[4]
                                                },
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {loading ? 'Authenticating...' : 'Sign in with Google'}
                                        </Button>
                                    )}

                                    {/* --- STEP 2: SETUP --- */}
                                    {step === 'SETUP_2FA' && (
                                        <Box>
                                            <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2, display: 'inline-block', mb: 2 }}>
                                                <QRCodeCanvas value={authUrl} size={160} />
                                            </Box>

                                            {/* Obfuscated Secret Display for Security */}
                                            <Box sx={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                bgcolor: theme.palette.action.hover, p: 1, borderRadius: 1, mb: 3,
                                                border: `1px solid ${theme.palette.divider}`
                                            }}>
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace', letterSpacing: 1, color: 'text.secondary' }}>
                                                    {tempSecret.substring(0, 4) + '••••••••' + tempSecret.substring(tempSecret.length - 4)}
                                                </Typography>
                                                <IconButton size="small" onClick={copyToClipboard} sx={{ ml: 1, color: 'action.active' }}>
                                                    <ContentCopyIcon fontSize="inherit" />
                                                </IconButton>
                                            </Box>

                                            <TextField
                                                fullWidth placeholder="Enter 6-digit code" variant="outlined"
                                                value={otpToken} onChange={(e) => setOtpToken(e.target.value)}
                                                sx={{ mb: 2 }}
                                                InputProps={{ // Use InputProps instead of nested styled '& .MuiOutlinedInput-root' for cleaner overrides
                                                    sx: {
                                                        fontSize: '1.2rem',
                                                        letterSpacing: 5,
                                                        textAlign: 'center',
                                                        bgcolor: 'background.default',
                                                        '& input': { textAlign: 'center' }
                                                    }
                                                }}
                                                inputProps={{ maxLength: 6 }}
                                                onPaste={(e) => e.preventDefault()}
                                            />

                                            <Button fullWidth variant="contained" color="warning" onClick={verifyCode} disabled={loading || otpToken.length !== 6} sx={{ borderRadius: 2, py: 1.2 }}>
                                                {loading ? 'Verifying...' : 'Enable 2FA & Login'}
                                            </Button>
                                        </Box>
                                    )}

                                    {/* --- STEP 3: VERIFY --- */}
                                    {step === 'VERIFY_2FA' && (
                                        <Box>
                                            <TextField
                                                fullWidth placeholder="000 000" variant="outlined" autoFocus
                                                value={otpToken} onChange={(e) => setOtpToken(e.target.value)}
                                                sx={{ mb: 3 }}
                                                InputProps={{
                                                    sx: {
                                                        fontSize: '1.5rem',
                                                        letterSpacing: 8,
                                                        fontWeight: 'bold',
                                                        bgcolor: 'background.default',
                                                        '& input': { textAlign: 'center' }
                                                    }
                                                }}
                                                inputProps={{ maxLength: 6 }}
                                                onPaste={(e) => e.preventDefault()}
                                            />

                                            <Button
                                                fullWidth variant="contained" color="success" size="large"
                                                onClick={verifyCode} disabled={loading || otpToken.length !== 6}
                                                sx={{ borderRadius: 2, py: 1.5, fontSize: '1rem' }}
                                            >
                                                {loading ? 'Verifying...' : 'Verify Identity'}
                                            </Button>

                                            <Button
                                                startIcon={<ArrowBackIcon />}
                                                sx={{ mt: 2, color: 'text.secondary', textTransform: 'none' }}
                                                onClick={() => { setStep('LOGIN'); setOtpToken(''); setError(''); }}
                                            >
                                                Back to Login
                                            </Button>

                                            {/* Reset 2FA Button */}
                                            <Button
                                                fullWidth
                                                variant="text"
                                                color="warning"
                                                sx={{ mt: 1, textTransform: 'none', fontSize: '0.85rem' }}
                                                onClick={async () => {
                                                    if (window.confirm("This will clear your 2FA setup. You'll need to scan a new QR code. Continue?")) {
                                                        try {
                                                            setLoading(true);
                                                            // Get admin email for new setup
                                                            const adminDoc = await getDoc(doc(db, "admins", userUid));
                                                            const adminEmail = adminDoc.data()?.email || '';

                                                            // Clear 2FA secret
                                                            await updateDoc(doc(db, "admins", userUid), {
                                                                twoFactorSecret: null,
                                                                isTwoFactorEnabled: false
                                                            });

                                                            setOtpToken('');
                                                            setError('');
                                                            initiateSetup(adminEmail);
                                                        } catch (err) {
                                                            setError(err.message || 'Failed to reset 2FA');
                                                        } finally {
                                                            setLoading(false);
                                                        }
                                                    }
                                                }}
                                                disabled={loading}
                                            >
                                                🔐 Lost Authenticator? Reset 2FA
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Paper>
                    </motion.div>
                </AnimatePresence>

                {/* Footer */}
                <Box sx={{ textAlign: 'center', mt: 4, opacity: 0.7 }}>
                    <Typography variant="caption" color="text.secondary">
                        &copy; {new Date().getFullYear()} Resido Technologies. Secured by 2FA.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default Login;